import type * as React from "react";
import { type FC, useCallback, useState } from "react";

import { MaterialInput } from "../../common/components/materialInput/MaterialInput";
import { sendResetPasswordRequest } from "../api/ResetPassword";
import { useInputState } from "../../common/hooks/useInputState";
import RecaptchaWidget from "../../common/components/RecaptchaWidget/RecaptchaWidget";
import { CAPTCHA_ENABLED } from "../../utils/constants";
import { useTranslation } from "react-i18next";

interface Props {
  initialEmail?: string;
  onSuccess: (globalErrors: string[]) => void;
  disableEdit?: boolean;
}

const EMAIL_FIELD_NAME = "email";

const ResetPasswordForm: FC<Props> = ({ initialEmail, onSuccess, disableEdit }) => {
  const { t } = useTranslation();

  const [email, onEmailChange] = useInputState(initialEmail);
  const [validationError, setValidationError] = useState("");

  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const captchaIsInvalid = (captchaValue: string) => {
    if (!CAPTCHA_ENABLED) {
      return false;
    }
    if (captchaValue === "") {
      setCaptchaError(t("recaptcha.challenge.field.empty"));
      return true;
    }
    if (!captchaValue) {
      setCaptchaError(t("recaptcha.challenge.field.wrong"));
      return true;
    }
    setCaptchaError("");
    return false;
  };

  const onCaptchaChange = (value: string | null) => {
    value = value || "";
    setCaptchaValue(value);
    captchaIsInvalid(value);
  };

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (captchaIsInvalid(captchaValue)) {
        return;
      }

      try {
        const data = await sendResetPasswordRequest(email, captchaValue);
        const requestHasErrors =
          data.validationResults[EMAIL_FIELD_NAME] || (data.globalErrors && data.globalErrors.length > 0);
        if (requestHasErrors) {
          if (data.validationResults[EMAIL_FIELD_NAME]) {
            setValidationError(data.validationResults[EMAIL_FIELD_NAME][0]);
          }
          if (data.globalErrors && data.globalErrors.length > 0) {
            setCaptchaError(t(data.globalErrors[0]));
          }
        } else {
          onSuccess(data.globalErrors);
        }
      } catch (err) {
        if (err instanceof Error) {
          setValidationError(err.message);
        }
      }
    },
    [email, onSuccess, captchaValue],
  );

  return (
    <>
      <p className={"mt-0 mb-3 forgot-password-description"}>{t("forgottenPwd.description")}</p>
      <form id="reset-password" onSubmit={onSubmit} className={"scha__reset-password-form"}>
        <div className={"mb-30 forgot-email-field"}>
          <MaterialInput
            label={t("forgottenPwd.email.label")}
            data-test-id={"reset-email-input"}
            name={EMAIL_FIELD_NAME}
            value={email}
            onChange={onEmailChange}
            error={!!validationError}
            helperLabel={t(validationError)}
            disabled={disableEdit}
          />
        </div>

        {CAPTCHA_ENABLED && <RecaptchaWidget onChange={onCaptchaChange} error={captchaError}></RecaptchaWidget>}
      </form>
    </>
  );
};

export default ResetPasswordForm;
